import {
  BoltIcon,
  CalendarIcon,
  ChatBubbleBottomCenterTextIcon,
  ClockIcon,
  MapPinIcon,
  PaperClipIcon,
  PencilSquareIcon,
  PlusIcon,
  RectangleStackIcon,
  XMarkIcon,
} from '@heroicons/react/16/solid';
// import { SparklesIcon } from '@heroicons/react/24/solid';
import { useEffect, useRef, useState } from 'react';
import { Dropdown, Tooltip } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import { useWapiAnalytics } from 'src/hooks/analytics/useWapiAnalytics.hook';
import { WAPI_EVENTS } from 'src/hooks/analytics/wapiAnalytics.constants';
import { useACLContext } from '../../../../../../providers/ACLProvider/ACL.hook';
import { useIntegrationsContext } from '../../../../../../providers/IntegrationsProvider/Integrations.context.ts';
import { useModalsContext } from '../../../../../../providers/ModalsProvider/Modals.context.ts';
import { eChannelType } from '../../../../../../providers/SocketsProvider/Sockets.enums';
import { isTikTokChannel } from '../../../../../../constants/channel.constants';
import { ChatEnum, ChatState } from '../../../../Chat.enum';
import { useChatContext } from '../../../../Chat.hook';
import { getModuleName } from '../../../../Chat.utils.ts';
import { useMessageBarContext } from '../../MessageBar.hook.ts';

export default function PlusMenu() {
  const { setSelectedAction, passDataToLegacyInput } = useMessageBarContext();

  const { selectedChat } = useChatContext();
  const [openDropdown, setOpenDropdown] = useState<boolean>(false);
  const dropdownActionRef = useRef<HTMLDivElement>(null);
  const { selectedLeadForChatModal } = useModalsContext();
  const { hasWaBusinessIntegration } = useIntegrationsContext();
  const { whatsAppApiAccess } = useACLContext();
  const { trackEvent } = useAnalytics();
  const { trackWapiEvent } = useWapiAnalytics();
  const isWapiChat = selectedChat?.integration === eChannelType.WHATSAPP_API;
  const isTiktokChat = isTikTokChannel(selectedChat?.integration);
  // Interactive messages require WAPI entitlement (Pro + Advanced on the
  // billing side), a connected WhatsApp Business integration (WABA
  // provisioned), and a WAPI-type chat (not WA Web / Messenger / Instagram).
  // The plan-entitlement check replaces the old `wapi_interactive-messages`
  // workspace feature flag — plan + integration is the authoritative gate
  // and keeps us from maintaining a separate flag in sync with billing.
  const canUseInteractiveMessages =
    whatsAppApiAccess && hasWaBusinessIntegration() && isWapiChat;

  const { t } = useTranslation(['chat/chat']);

  function handleOpenDropdown(event: React.MouseEvent<HTMLLabelElement>) {
    event.preventDefault();
    setOpenDropdown((prev) => !prev);
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownActionRef.current &&
        !dropdownActionRef.current.contains(event.target as Node)
      ) {
        setOpenDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Dropdown vertical="top" open={openDropdown} ref={dropdownActionRef}>
      <Tooltip message={t('plusMenu.label')}>
        <Dropdown.Toggle
          className="MessageBar__Toggle"
          color="ghost"
          size="xs"
          onClick={handleOpenDropdown}
          data-tour="chat-plus-menu"
        >
          {openDropdown ? (
            <XMarkIcon className="MessageBar__Icon" />
          ) : (
            <PlusIcon className="MessageBar__Icon" />
          )}
        </Dropdown.Toggle>
      </Tooltip>

      {openDropdown && (
        <Dropdown.Menu
          className={`${hasWaBusinessIntegration() ? 'w-72' : 'w-52'}`}
        >
          <Dropdown.Item
            className="ActionButtons__Button"
            onClick={() => {
              setOpenDropdown(false);
              passDataToLegacyInput(ChatEnum.NOTES);
            }}
          >
            <PencilSquareIcon className="MessageBar__Icon" />
            <p>{t('plusMenu.addNote')}</p>
          </Dropdown.Item>

          <Dropdown.Item
            className="ActionButtons__Button"
            onClick={() => {
              setOpenDropdown(false);
              setSelectedAction(ChatEnum.EVENTS);
              trackEvent('front.event.schedule.create_btn.click', {
                displayName: 'Agendar evento',
                description:
                  'Cuántas veces hubo la intención de crear un evento nuevo desde la conversación del lead',
                ctaType: getModuleName(),
              });
            }}
          >
            <CalendarIcon className="MessageBar__Icon" />
            <p>{t('plusMenu.scheduleEvent')}</p>
          </Dropdown.Item>

          {selectedLeadForChatModal?.chat?.integration !==
            eChannelType.WHATSAPP_WEB &&
            selectedChat?.state === ChatState.TEMPLATE && (
              <Dropdown.Item
                className="ActionButtons__Button"
                onClick={() => {
                  setOpenDropdown(false);
                  setSelectedAction(ChatEnum.TEMPLATES);
                }}
              >
                <ChatBubbleBottomCenterTextIcon className="MessageBar__Icon" />
                <p>{t('plusMenu.selectTemplate')}</p>
              </Dropdown.Item>
            )}

          {selectedChat?.state === ChatState.ACTIVE && (
            <>
              <Dropdown.Item
                className="ActionButtons__Button"
                onClick={() => {
                  setOpenDropdown(false);
                  setSelectedAction(ChatEnum.DRIVE);
                }}
              >
                <PaperClipIcon className="MessageBar__Icon" />
                <p>{t('plusMenu.uploadFile')}</p>
              </Dropdown.Item>

              {!isTiktokChat && (
                <Dropdown.Item
                  className="ActionButtons__Button"
                  onClick={() => {
                    setOpenDropdown(false);
                    setSelectedAction(ChatEnum.MAPS);
                  }}
                >
                  <MapPinIcon className="MessageBar__Icon" />
                  <p>{t('plusMenu.sendLocation')}</p>
                </Dropdown.Item>
              )}

              <Dropdown.Item
                className="ActionButtons__Button"
                onClick={() => {
                  passDataToLegacyInput(ChatEnum.QUICK_ACTIONS);
                  setOpenDropdown(false);
                }}
              >
                <BoltIcon className="MessageBar__Icon" />
                <p>{t('plusMenu.quickActions')}</p>
              </Dropdown.Item>

              {canUseInteractiveMessages && (
                <Dropdown.Item
                  className="ActionButtons__Button"
                  onClick={() => {
                    setOpenDropdown(false);
                    setSelectedAction(ChatEnum.INTERACTIVE);
                    trackWapiEvent(WAPI_EVENTS.CHAT_INTERACTIVE_OPEN, {
                      conversationId: selectedChat?.id ?? '',
                      recipientNumber: selectedChat?.leadId ?? '',
                    });
                  }}
                >
                  <RectangleStackIcon className="MessageBar__Icon" />
                  <p>{t('plusMenu.interactiveMessage')}</p>
                </Dropdown.Item>
              )}

              <Dropdown.Item
                className="ActionButtons__Button"
                onClick={() => {
                  setOpenDropdown(false);
                  passDataToLegacyInput(ChatEnum.PROGRAMMED_MESSAGES);
                }}
              >
                <ClockIcon className="MessageBar__Icon" />
                <p>{t('plusMenu.scheduleMessage')}</p>
              </Dropdown.Item>
            </>
          )}
          {hasWaBusinessIntegration() &&
          selectedChat?.integration === eChannelType.WHATSAPP_API ? (
            <Dropdown.Item
              className="ActionButtons__Button"
              data-tour="chat-plus-send-template"
              onClick={() => {
                setOpenDropdown(false);
                setSelectedAction(ChatEnum.WABTEMPLATES);
              }}
            >
              <img
                src="/images/agenda/RectangleGroupIcon.svg"
                alt="plantilla"
                className="w-6"
              />
              Enviar plantilla (WA Bussiness)
            </Dropdown.Item>
          ) : null}
        </Dropdown.Menu>
      )}
    </Dropdown>
  );
}
